// An opened meeting's recording, transcript, title and "Move to a calendar
// meeting…", as pure functions; and the Past list's older pages.
//
// Recording: the player is drawn only for audio the page can play. A newer
// server says so on the meeting (`recording_playable`); for an older one the
// page asks the file route for its first byte (a 404 = not there), except
// for a path inside the agent's container (a voice note an agent filed),
// which that route never serves. The Past list's "Has a recording" mark
// follows the same fields, so the list never promises audio the panel lacks.
//
// Transcript: its text is read on its own (GET …/meetings/{id}/transcript)
// when the fold is opened; the list never carries it. The fold is offered
// when something suggests a transcript; a saved path alone is never shown.
//
// Moving: only an entry the AGENT filed itself can be moved onto a calendar
// meeting, by the server's own test (absorb refuses the rest): source
// "recording", no recording of the app's (`recording_status` is only ever
// set by the app's recorder, and a voice note Lattice copied in leaves it
// empty; no recording segments), and a recording path, if any, that is a
// voice note's (in the agent's container, or in the meeting's voice-notes
// folder). The choice: calendar meetings that had started when the entry
// was filed, real meetings before blocks and all-day events.
//
// Older pages: the page loads the newest PAGE meetings; "Show older" asks
// for the PAGE before the last one loaded, in the server's own order
// (start, else created, newest first, compared as text exactly as the
// server sorts), until a page comes back short or brings nothing new.
//
// No React. Self-test: detailModel.selftest.ts.

import type { Meeting } from "@/api/meetings";
import { isDateOnly, startOfDay, toDate } from "@/lib/format";
import { isBlock } from "./listModel";
import { matchesSearch } from "./pastModel";
import type { NameBook } from "./people";

// ─── recording ───

/** Where an agent's voice notes live in its container: the file route never serves these. */
const IN_CONTAINER = /^\/opt\/openclaw\//;

/** The page asked the file route: there, gone, or it could not tell (a network error lets the player try). */
export type FileProbe = "found" | "missing" | "unknown";

/** What the Recording section shows: the player, the one line instead of it, nothing yet (asking), or no section. */
export type PlayerView = "player" | "missing" | "wait" | "none";

/** True when only asking the file route can tell whether the recording plays. */
export function needsProbe(m: Pick<Meeting, "recording_path" | "recording_status" | "recording_playable">): boolean {
  if (!m.recording_path || m.recording_status === "uploading") return false;
  if (typeof m.recording_playable === "boolean") return false;
  return !IN_CONTAINER.test(m.recording_path);
}

export function playerView(
  m: Pick<Meeting, "recording_path" | "recording_status" | "recording_playable">,
  probe: FileProbe | null = null,
): PlayerView {
  if (!m.recording_path || m.recording_status === "uploading") return "none";
  if (m.recording_playable === false) return "missing";
  if (m.recording_playable === true) return "player";
  if (IN_CONTAINER.test(m.recording_path)) return "missing";
  if (probe === "missing") return "missing";
  return probe ? "player" : "wait";
}

export const MISSING_AUDIO = "The audio wasn't saved where this page can play it.";

/** Whether the opened meeting has a Recording section at all (the player, or the one line instead of it). */
export const hasRecordingSection = (m: Pick<Meeting, "recording_path" | "recording_status" | "recording_playable">) =>
  playerView(m) !== "none";

/**
 * The Past list's "Has a recording" mark: a recording not known to be
 * unplayable from the fields alone (the list never asks the file route).
 */
export function recordingMark(m: Pick<Meeting, "recording_path" | "recording_playable">): boolean {
  if (!m.recording_path || m.recording_playable === false) return false;
  return m.recording_playable === true || !IN_CONTAINER.test(m.recording_path);
}

// ─── transcript ───

/**
 * Whether to offer the fold: something suggests a transcript (a saved
 * path, a finished recording, a voice note's audio, a notes-bot call that
 * finished). What it holds is read when it is opened.
 */
export function transcriptOffered(m: Pick<Meeting, "transcript_path" | "recording_path" | "recording_status" | "bot">): boolean {
  if (m.transcript_path?.trim() || m.recording_status === "ready" || m.bot === "done") return true;
  return !!m.recording_path && m.recording_status == null;
}

/** The text the transcript route answered, trimmed; null when none was saved. */
export function transcriptText(answer: { text?: string | null } | undefined): string | null {
  const text = answer?.text?.trim();
  return text ? text : null;
}

// ─── title ───

/** The server's limit on a title. */
export const TITLE_MAX = 512;

/**
 * Only a meeting that is not from the calendar keeps a new title: the
 * calendar sync writes a calendar meeting's title from Google every time.
 */
export const titleEditable = (m: Pick<Meeting, "source">) => m.source !== "calendar";

/** The title to save, or null when there is nothing to save (unchanged, or empty: a title is required). */
export function titleToSave(typed: string, current: string): string | null {
  const next = typed.replace(/\s+/g, " ").trim().slice(0, TITLE_MAX);
  if (!next || next === current.trim()) return null;
  return next;
}

// ─── moving an agent's entry onto a calendar meeting ───

/** A recording path that is a voice note's: in the agent's container (an older entry), or in the meeting's voice-notes folder. */
const VOICE_NOTE_PATH = /\/voice-notes\//;

/** An entry the agent filed itself, by the server's own test (see the header). */
export function agentFiled(
  m: Pick<Meeting, "source" | "recording_status" | "recording_segments" | "recording_path">,
): boolean {
  if (m.source !== "recording" || m.recording_status != null) return false;
  if (m.recording_segments?.length) return false;
  const path = m.recording_path ?? "";
  return !path || IN_CONTAINER.test(path) || VOICE_NOTE_PATH.test(path);
}

/** The day an entry is about: its start, else when it was filed. */
function dayOf(m: Pick<Meeting, "start_at" | "created_at">): Date | null {
  const d = toDate(m.start_at) ?? toDate(m.created_at);
  return d ? startOfDay(d) : null;
}

/** The latest a meeting it is about can have started: when it was filed, or its own start if that is later. */
function latestStart(m: Pick<Meeting, "start_at" | "created_at">): number | null {
  const times = [toDate(m.created_at), toDate(m.start_at)].flatMap((d) => (d ? [d.getTime()] : []));
  return times.length ? Math.max(...times) : null;
}

/** A block on the operator's own calendar, or an all-day event: listed after the real meetings. */
const quiet = (m: Meeting) => isBlock(m) || isDateOnly(m.start_at);

/**
 * The calendar meetings an entry can be moved onto: the same agent's, on
 * the entry's day or the day before (a voice note often comes after its
 * meeting, even hours later), that had started by the time it was filed (a
 * note is never about a meeting still ahead), never one a booker
 * cancelled; real meetings newest first (the one just before the note on
 * top), then blocks and all-day events; then narrowed by the typed words
 * (title, people, addresses).
 */
export function moveCandidates(entry: Meeting, all: readonly Meeting[], query = "", book: NameBook = new Map()): Meeting[] {
  const day = dayOf(entry);
  const latest = latestStart(entry);
  if (!day || latest === null) return [];
  // The day before at midnight, to the end of the entry's day (DST-proof: by the calendar).
  const from = new Date(day.getFullYear(), day.getMonth(), day.getDate() - 1).getTime();
  const to = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1).getTime();
  const start = (m: Meeting) => toDate(m.start_at)?.getTime() ?? 0;
  return all
    .filter((m) => m.source === "calendar" && m.agent_template === entry.agent_template && m.id !== entry.id)
    .filter((m) => m.skip_reason !== "booker_cancelled")
    .filter((m) => {
      const t = toDate(m.start_at)?.getTime();
      return t !== undefined && t >= from && t < to && t <= latest;
    })
    .filter((m) => matchesSearch(m, query, book))
    .sort((a, b) => Number(quiet(a)) - Number(quiet(b)) || start(b) - start(a));
}

/**
 * Where the window the dialog reads ends: two days after the entry's day
 * began (one past its end, so a time with another offset is never cut off
 * by the server's text compare). Null when the entry has no day.
 */
export function moveWindowEnd(entry: Pick<Meeting, "start_at" | "created_at">): string | null {
  const day = dayOf(entry);
  return day ? new Date(day.getFullYear(), day.getMonth(), day.getDate() + 2).toISOString() : null;
}

// ─── the Past list's older pages ───

/** Rows in each page the page asks for (the newest page and every older one). */
export const PAGE = 300;

/** The server's sort key for a row: start, else when it was made (text, as the server compares it). */
export const sortKey = (m: Pick<Meeting, "start_at" | "created_at">) => m.start_at ?? m.created_at ?? "";

/** Where the first older page starts: the last row of a full newest page, else null (everything is loaded). */
export function olderCursor(main: readonly Pick<Meeting, "start_at" | "created_at">[] | undefined, page = PAGE): string | null {
  if (!main || main.length < page) return null;
  return sortKey(main[main.length - 1]) || null;
}

/**
 * The cursor for the page after `rows` (asked for with `param`), or
 * undefined when there is no more: a short page, a page with nothing not
 * already seen, or a last row no older than the cursor that asked for it.
 */
export function nextCursor(rows: readonly Meeting[], seen: ReadonlySet<string>, param: string, page = PAGE): string | undefined {
  if (rows.length < page) return undefined;
  if (!rows.some((r) => !seen.has(r.id))) return undefined;
  const key = sortKey(rows[rows.length - 1]);
  if (!key || key >= param) return undefined;
  return key;
}

/** The newest page, then each older page's rows not already there, in order. */
export function mergePages(main: readonly Meeting[], older: readonly (readonly Meeting[])[]): Meeting[] {
  if (!older.length) return main as Meeting[];
  const seen = new Set(main.map((m) => m.id));
  const out = [...main];
  for (const page of older) {
    for (const m of page) {
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      out.push(m);
    }
  }
  return out;
}
